/**
 * useCopyFeedback — Shared hook for copy-to-clipboard with visual feedback.
 *
 * Handles:
 * - Modern Clipboard API (navigator.clipboard.writeText)
 * - Fallback for non-HTTPS / older browsers (execCommand)
 * - Timed "copied" feedback state with automatic reset
 * - Cleanup of timers on unmount
 *
 * Used by: CommunityProfileHeader, CommunityProfileUserView,
 * CommunityProfileSettingsPage, PlaylistSharedView, PlaylistSharePreview
 */

import {
  useState, useRef, useCallback, useEffect,
} from 'react'
import copyTextToClipboard from './clipboard-utils'

const COPY_FEEDBACK_MS = 2000

interface UseCopyFeedbackOptions {
  /** Duration in ms to show the "copied" state. Defaults to 2000. */
  feedbackMs?: number
  /** Optional callback invoked on clipboard write failure. */
  onError?: () => void
}

interface UseCopyFeedbackReturn {
  /** Whether the "copied" feedback is active. */
  copied: boolean
  /** Call with the text to copy. Returns a promise. */
  handleCopy: (text: string) => Promise<void>
}

export default function useCopyFeedback (
  options: UseCopyFeedbackOptions = {},
): UseCopyFeedbackReturn {
  const { feedbackMs = COPY_FEEDBACK_MS, onError } = options
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current)
  }, [])

  const handleCopy = useCallback(async (text: string) => {
    if (typeof window === 'undefined') return
    try {
      await copyTextToClipboard(text)
      setCopied(true)
      if (timerRef.current) clearTimeout(timerRef.current)
      timerRef.current = setTimeout(() => {
        setCopied(false)
        timerRef.current = null
      }, feedbackMs)
    } catch {
      if (onError) onError()
    }
  }, [feedbackMs, onError])

  return { copied, handleCopy }
}
